import { FC, useContext, useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import { CheckboxV2, ExternalLink, FormGroup, Icons, Progress, defaultTheme, Text } from '@agro-club/agroclub-shared'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import formatTime from 'helpers/formatTime'
import useTimer from 'hooks/useTimer'
import i18n from 'i18n'

import { useAFormHandler, useAnalyticsContext } from 'analytics/hooks'
import { AuthContext } from 'modules/context/AuthContext'
import { TokenResponse } from 'modules/domain/auth/managers'
import { saveAccessToken, saveRefreshToken } from 'modules/domain/auth/repository'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { useMediaQuery } from 'react-responsive'
import { AButton } from 'views/components/Analytics'
import {
  BackIcon,
  CodeWrapper,
  Controls,
  ErrorText,
  FormWrapper,
  InputLabel,
  InputStyled,
  InputWrapper,
  RetryBtn,
  RetryDelay,
  SubmitButtonWrapper,
} from './styled'
import styled from 'styled-components'

type FormFields = {
  code: string
  nda_signed: boolean
}

const NDA_LINK =
  'https://storage.yandexcloud.net/docs.agro.club/%D0%9E%D0%BD%D0%BB%D0%B0%D0%B8%CC%86%D0%BD%20%D0%BE%D0%B1%D1%8F%D0%B7%D0%B0%D1%82%D0%B5%D0%BB%D1%8C%D1%81%D1%82%D0%B2%D0%BE%20%D0%BE%20%D0%BD%D0%B5%D1%80%D0%B0%D0%B7%D0%B3%D0%BB%D0%B0%D1%88%D0%B5%D0%BD%D0%B8%D0%B8%20%D0%9A%D0%A2.pdf'

const NdaIcon = styled(Icons.IconContract)`
  margin-left: -2px;
  path {
    fill: ${defaultTheme.color.primary600};
  }
`
const NdaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: 16px;
`

const validationSchema = Yup.object({
  code: Yup.string()
    .required(i18n.t('validation:code_required'))
    .test('len', i18n.t('validation:code_length'), (val) => !!val && val.length === 6),
  nda_signed: Yup.boolean(),
})

const OtpForm: FC<{ phone: string; ndaSigned: boolean; resend: () => void; onBack: () => void }> = ({
  resend,
  phone,
  ndaSigned,
  onBack,
}) => {
  const { track } = useAnalyticsContext()
  const { onLogin } = useContext(AuthContext)
  const { formProgress, formHandler } = useAFormHandler('login')

  const { t } = useTranslation(['auth', 'common'])

  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const formik = useFormik<FormFields>({
    initialValues: {
      code: '',
      nda_signed: false,
    },
    validationSchema,
    onSubmit: formHandler(
      async () => {
        const response = await apiClient.post<TokenResponse>(endpoints.loginCRM(), {
          code: formik.values.code,
          phone,
          nda_signed: formik.values.nda_signed,
        })
        saveRefreshToken(response.refresh)
        saveAccessToken(response.access)
        await onLogin()
      },
      {
        onError: (error) => {
          const { errors } = error
          if (errors.code || errors.nda_signed) {
            formik.setErrors(errors)
          } else if (errors.non_field_errors) {
            formik.setErrors({ code: errors.non_field_errors?.[0] })
          } else {
            formik.setErrors({ code: error.detail || 'Unexpected error' })
          }
        },
      },
    ),
  })

  const { seconds, startTimer } = useTimer({
    initialSeconds: 60,
  })
  const time = formatTime(seconds)

  const handleGetCode = () => {
    resend()
    startTimer()

    track('click|getCode')
  }

  const isMobile = useMediaQuery({
    query: `(max-width: ${defaultTheme.queries.mobile})`,
  })
  const size = isMobile ? 'largeMobile' : 'big'

  return (
    <FormWrapper>
      <CodeWrapper>
        <InputLabel>{t('auth:smsCode')}</InputLabel>
        <Controls>
          <InputWrapper>
            <FormGroup error={!isMobile && formProgress === Progress.ERROR ? formik.errors.code : undefined}>
              <InputStyled
                type="text"
                {...formik.getFieldProps('code')}
                onChange={({ target: { value } }) => {
                  formik.setFieldValue('code', value.replace(/\s*/g, ''))
                }}
                invalid={!!formik.errors.code}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') formik.submitForm()
                }}
                autoFocus
              />
            </FormGroup>
          </InputWrapper>
          {seconds > 0 ? (
            <RetryDelay>{time}</RetryDelay>
          ) : (
            <RetryBtn disabled={formProgress === 'WORK'} onClick={handleGetCode}>
              {t('auth:sendAnotherCode')}
            </RetryBtn>
          )}
        </Controls>
        {!ndaSigned && (
          <NdaWrapper>
            <ExternalLink href={NDA_LINK} target="_blank">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <NdaIcon />
                <Text>{t('ndaLabel')}</Text>
              </div>
            </ExternalLink>
            <FormGroup error={formik.errors.nda_signed}>
              <CheckboxV2
                id="nda_signed"
                onChange={(e) => {
                  console.log(e.target.checked)
                  formik.setFieldValue('nda_signed', !!e.target.checked)
                }}
                checked={formik.values.nda_signed}
                label={t('agreeText')}
              />
            </FormGroup>
          </NdaWrapper>
        )}
        {isMobile && (
          <ErrorText typography="bodySmall">
            {formProgress === Progress.ERROR ? formik.errors.code : undefined}
          </ErrorText>
        )}
      </CodeWrapper>

      <SubmitButtonWrapper>
        <AButton id="back" Icon={BackIcon} color="secondary" onClick={onBack} size={size} />

        <AButton
          id="send"
          style={{ width: '100%' }}
          size={size}
          type="submit"
          color="primary"
          disabled={!formik.isValid || !formik.dirty}
          progress={formProgress}
          onClick={() => formik.submitForm()}
        >
          {t('auth:send')}
        </AButton>
      </SubmitButtonWrapper>
    </FormWrapper>
  )
}

export default OtpForm
